/* __next_internal_client_entry_do_not_use__ ScrollButton,Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { findComponent } from 'lib/utils/object-utils';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { TabContextProvider, useTab } from 'helpers/Context/TabContext';
import { SvgIcon } from 'helpers/SvgIcon';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { useScrollableTabs } from 'lib/hooks/useScrollableTabs';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
export const ScrollButton = ({ direction, onClick, tabListId = 'tab-list' })=>{
    const { tabArrows } = TAILWIND_VARIANTS();
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };
    return /*#__PURE__*/ _jsx("button", {
        className: tabArrows({
            right: direction === 'right'
        }),
        onClick: onClick,
        onKeyDown: handleKeyDown,
        "aria-label": `Scroll tabs ${direction}`,
        "aria-controls": tabListId,
        tabIndex: 0,
        children: /*#__PURE__*/ _jsx(SvgIcon, {
            icon: `arrow-dash-${direction}`,
            size: "s",
            viewBox: '0 0 20 20',
            fill: "currentColor"
        })
    });
};
const TabContent = (props)=>{
    const isEditing = useIsEditing();
    const { DynamicPlaceholderId, RenderingIdentifier } = props?.params || {};
    const { activeTab, setActiveTab } = useTab();
    const { containerRef, isScrollable, isLeftArrowVisible, isRightArrowVisible, handleScroll } = useScrollableTabs();
    const phKey = `tab-${DynamicPlaceholderId}`;
    const tabItems = findComponent(props.rendering, 'TabItem');
    const { base, tabsContainerWrapper, tabsWrapper, tabLinkWrapper, tabLink } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("section", {
        className: base(),
        "data-component": "authorable/shared/lists/Tab",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-tab-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: tabsContainerWrapper(),
                children: [
                    isScrollable && isLeftArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                        direction: "left",
                        onClick: ()=>handleScroll('left')
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: tabsWrapper(),
                        ref: containerRef,
                        role: "tablist",
                        id: "tab-list",
                        "aria-label": "Tab navigation",
                        ...getTestProps(`tab-ul`),
                        children: tabItems.map((item, index)=>{
                            const isActive = activeTab === item.uid;
                            const styles = parseStyleParams(item.params, [
                                'cta1'
                            ]);
                            const ctaVarient = styles.cta1?.ctaVariant || 'primary';
                            return /*#__PURE__*/ _jsx("li", {
                                className: tabLinkWrapper({
                                    isActive
                                }),
                                ...getTestProps(`tab-content-${index + 1}`),
                                role: "presentation",
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "default",
                                    className: tabLink({
                                        style: ctaVarient,
                                        isActive
                                    }),
                                    ctaStyle: {
                                        ...getCtaStyle(styles.cta1, ctaVarient)
                                    },
                                    ctaVariant: "custom",
                                    field: {
                                        href: '#',
                                        anchor: 'tab-content-' + item.uid
                                    },
                                    onClick: (e)=>{
                                        e.preventDefault();
                                        setActiveTab(item.uid || '');
                                    },
                                    id: `tab-${item.uid}`,
                                    role: "tab",
                                    "aria-controls": `tab-content-${item.uid}`,
                                    "aria-selected": isActive,
                                    ...getTestProps(`tab-link-${index + 1}`),
                                    children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        field: item.fields?.title,
                                        ...getTestProps(`tab-title-${index + 1}`)
                                    })
                                })
                            }, item.uid);
                        })
                    }),
                    isScrollable && isRightArrowVisible && /*#__PURE__*/ _jsx(ScrollButton, {
                        direction: "right",
                        onClick: ()=>handleScroll('right')
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                name: phKey,
                rendering: props.rendering,
                ...getTestProps(`tab-ph-${props?.rendering?.uid}`)
            })
        ]
    });
};
const Tab = (props)=>{
    const tabItems = findComponent(props.rendering, 'TabItem');
    return /*#__PURE__*/ _jsx(TabContextProvider, {
        tabs: tabItems,
        children: /*#__PURE__*/ _jsx(TabContent, {
            ...props
        })
    });
};
export const Default = withStandardComponentWrapper(Tab, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [],
        tabsContainerWrapper: [
            'container',
            'flex',
            'items-center',
            'gap-space-between-micro',
            'relative'
        ],
        tabArrows: [
            'absolute',
            'py-4',
            'px-0',
            'w-[44px]',
            'h-[68px]'
        ],
        tabsWrapper: [
            'container',
            'overflow-x-auto',
            'bg-component-tab-color-bg',
            'rounded-component-tab-border-radius',
            'flex',
            'px-component-tab-layout-container-padding-x',
            'py-component-tab-layout-container-padding-y'
        ],
        tabLinkWrapper: [
            'gap-spacing-spacing-8',
            'border-l-component-tab-border-width-left',
            'border-r-component-tab-border-width-right',
            'border-t-component-tab-border-width-top',
            'border-b-component-tab-border-width-bottom',
            'border-component-tab-color-border',
            'hover:border-component-tab-color-border-hover',
            'rounded-component-tab-border-radius',
            'focus:ring-color-border-border-focus',
            'focus:border-component-tab-color-border-focused'
        ],
        tabLink: [
            'flex',
            'items-center',
            'justify-center',
            'px-spacing-spacing-24',
            'py-spacing-spacing-16',
            'min-w-component-tab-layout-min-width',
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'leading-5',
            'font-semibold',
            'text-component-tab-color-text',
            'bg-component-tab-color-bg',
            'hover:text-component-tab-color-text-hover',
            'hover:bg-component-tab-color-bg-hover',
            'focus:bg-component-tab-color-bg-focused',
            'focus:text-component-tab-color-text-focused',
            'whitespace-nowrap'
        ]
    },
    variants: {
        isActive: {
            true: {
                tabLinkWrapper: [
                    'border-component-tab-color-border-active'
                ],
                tabLink: [
                    'text-component-tab-color-text-active',
                    'bg-component-tab-color-bg-active'
                ]
            }
        },
        disabled: {
            true: {
                tabArrows: [
                    'opacity-50',
                    'cursor-not-allowed',
                    'hover:bg-transparent'
                ]
            }
        },
        style: {
            tertiary: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            tertiaryInverse: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            primary: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            secondary: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            primaryInverse: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            secondaryInverse: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            primaryOnCanvas: {
                tabLink: [
                    'gap-spacing-spacing-8'
                ]
            },
            custom: {
                tabLink: []
            }
        },
        right: {
            true: {
                tabArrows: [
                    'right-[-1px]',
                    'bg-gradient-to-l',
                    'from-color-bg-bg to-color-white-alpha-0 from-[9.61%] to-100%'
                ]
            },
            false: {
                tabArrows: [
                    'left-[-1px]',
                    'bg-gradient-to-r',
                    'from-color-bg-bg to-color-white-alpha-0 from-[9.61%] to-100%'
                ]
            }
        }
    }
});
